import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useOptionalClientViewGeolocation } from "../context/ClientViewGeolocationProvider";
import {
  useAutoRequestClientViewGeolocation,
  useMapUserLocationLayer,
} from "../hooks/useClientViewGeolocation";
import { formatPublicListingPrice } from "../utils/listingDisplay";
import { getPrimaryListingImageUrl, PROPERTY_PLACEHOLDER_SRC } from "../utils/listingImageDisplay";
import { API_BASE_URL } from "../../config/apiBaseUrl.js";
import { resolvePublicImageUrl } from "../../utils/resolvePublicImageUrl";
import { runtimeListingPath } from "../utils/routes";
import { DEFAULT_MAP_STYLE, MAP_STYLE_URLS } from "../../constants/mapStyles";
import CasaMapaMapControls from "../../components/casamap/map/CasaMapaMapControls";
import {
  BOUNDARY_FILL_LAYER,
  BOUNDARY_HIGHLIGHT_CORE,
  BOUNDARY_HIGHLIGHT_FILL,
  BOUNDARY_HIGHLIGHT_HALO,
  BOUNDARY_HIGHLIGHT_LINE,
  BOUNDARY_LINE_LAYER,
  BOUNDARY_SOURCE,
  buildBoundaryFillOpacityExpression,
  buildBoundaryHighlightFilter,
  buildBoundaryLineOpacityExpression,
  extendLngLatBounds,
  findBoundaryFeature,
  normalizeBoundaryId,
} from "../utils/boundaryHighlight";

const BASEMAP_STYLES = {
  light: MAP_STYLE_URLS.light,
  streets: MAP_STYLE_URLS.streets,
  satellite: MAP_STYLE_URLS.satellite,
};

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || "";
const DEFAULT_CENTER = [-115.14, 36.17];
const DEFAULT_ZOOM = 10;
const MAX_FIT_DISTANCE_DEG = 5;

const LISTINGS_SOURCE = "cv-listings";
const LISTINGS_HALO = "cv-listings-halo";
const LISTINGS_POINT = "cv-listings-point";
const LISTINGS_CLUSTERS = "cv-listings-clusters";
const LISTINGS_CLUSTER_COUNT = "cv-listings-cluster-count";
const AGGREGATE_SOURCE = "cv-listing-aggregates";
const AGGREGATE_CIRCLES = "cv-listing-aggregates-circles";
const AGGREGATE_COUNT = "cv-listing-aggregates-count";

const CLUSTER_COLOR = "#087f74";
const APPROXIMATE_LOCATION_COLOR = "#0f766e";
const MISSING_ADDRESS_COLOR = APPROXIMATE_LOCATION_COLOR;
const CLUSTER_MAX_ZOOM = 14;
const CLUSTER_RADIUS = 52;
const AGGREGATE_PIN_ZOOM = 14;

function aggregatesToGeoJson(aggregates = [], cellSizeDegrees = null) {
  return {
    type: "FeatureCollection",
    features: (aggregates || [])
      .filter(
        (cluster) =>
          Number.isFinite(Number(cluster?.lat)) &&
          Number.isFinite(Number(cluster?.lng)) &&
          Number(cluster?.count) >= 2
      )
      .map((cluster) => ({
        type: "Feature",
        properties: {
          cluster_id: cluster.id || `${cluster.lat},${cluster.lng}`,
          count: Number(cluster.count) || 0,
          count_label:
            Number(cluster.count) >= 1000
              ? `${Math.round(Number(cluster.count) / 100) / 10}k`
              : String(Number(cluster.count) || 0),
          cell_size_degrees: cellSizeDegrees,
          cell_west: cluster.bounds?.west ?? null,
          cell_south: cluster.bounds?.south ?? null,
          cell_east: cluster.bounds?.east ?? null,
          cell_north: cluster.bounds?.north ?? null,
        },
        geometry: {
          type: "Point",
          coordinates: [Number(cluster.lng), Number(cluster.lat)],
        },
      })),
  };
}

function setLayerVisibility(map, layerId, visible) {
  if (!map?.getLayer?.(layerId)) {
    return;
  }
  map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
}

function safeMapResize(map) {
  if (!map) return;
  try {
    const canvas = typeof map.getCanvas === "function" ? map.getCanvas() : null;
    const container = typeof map.getContainer === "function" ? map.getContainer() : null;
    if (!canvas || !container || !container.isConnected) return;
    map.resize();
  } catch {
    // Map may already be removed (SPA remount / teardown race).
  }
}

function featuresNearCenter(features, refCenter) {
  const [cLng, cLat] = refCenter;
  return features.filter((f) => {
    const [lng, lat] = f.geometry.coordinates;
    return (
      Math.abs(lat - cLat) <= MAX_FIT_DISTANCE_DEG &&
      Math.abs(lng - cLng) <= MAX_FIT_DISTANCE_DEG
    );
  });
}

function quantile(sorted, percentile) {
  if (!sorted.length) {
    return 0;
  }
  const index = Math.min(sorted.length - 1, Math.max(0, Math.floor((sorted.length - 1) * percentile)));
  return sorted[index];
}

function trimOutlierFeatures(features) {
  if (!features || features.length < 8) {
    return features || [];
  }
  const lngs = features.map((feature) => feature.geometry.coordinates[0]).sort((a, b) => a - b);
  const lats = features.map((feature) => feature.geometry.coordinates[1]).sort((a, b) => a - b);
  const west = quantile(lngs, 0.05);
  const east = quantile(lngs, 0.95);
  const south = quantile(lats, 0.05);
  const north = quantile(lats, 0.95);
  const kept = features.filter((feature) => {
    const [lng, lat] = feature.geometry.coordinates;
    return lng >= west && lng <= east && lat >= south && lat <= north;
  });
  return kept.length ? kept : features;
}

function listingHasCardAddress(listing) {
  const address =
    listing?.address?.display ||
    listing?.address?.street ||
    listing?.addressText ||
    listing?.streetAddress ||
    null;
  return Boolean(address && String(address).trim());
}

function listingCoordinates(listing) {
  const latitude = Number(
    listing?.latitude ?? listing?.location?.latitude ?? listing?.lat
  );
  const longitude = Number(
    listing?.longitude ?? listing?.location?.longitude ?? listing?.lng
  );
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }
  return [longitude, latitude];
}

function popupIsOpen(popup) {
  if (!popup) {
    return false;
  }
  if (typeof popup.isOpen === "function") {
    return popup.isOpen();
  }
  return Boolean(popup._map);
}

function queryRenderedListingCoordinates(map, listingId) {
  if (!map || listingId == null || !map.getLayer?.(LISTINGS_POINT)) {
    return null;
  }
  try {
    const features = map.queryRenderedFeatures({ layers: [LISTINGS_POINT] }) || [];
    const match = features.find((feature) => String(feature?.properties?.id) === String(listingId));
    const coords = match?.geometry?.coordinates;
    if (
      Array.isArray(coords) &&
      Number.isFinite(Number(coords[0])) &&
      Number.isFinite(Number(coords[1]))
    ) {
      return [Number(coords[0]), Number(coords[1])];
    }
  } catch {
    return null;
  }
  return null;
}

function listingsToGeoJson(listings = []) {
  return {
    type: "FeatureCollection",
    features: listings
      .map((listing) => {
        const coordinates = listingCoordinates(listing);
        if (!coordinates) {
          return null;
        }
        return {
          type: "Feature",
          id: String(listing.id),
          geometry: {
            type: "Point",
            coordinates,
          },
          properties: {
            id: String(listing.id),
            title: listing.title || "Listing",
            price: listing.price,
            currency: listing.currency || "USD",
            thumbnailUrl: getPrimaryListingImageUrl(listing),
            bedrooms: listing.bedrooms,
            bathrooms: listing.bathrooms,
            hasAddress: listingHasCardAddress(listing) ? 1 : 0,
            approximateLocation:
              !listingHasCardAddress(listing) ||
              String(listing?.location?.precision || "").toLowerCase() === "municipality" ||
              String(listing?.location?.precision || "").toLowerCase() === "city"
                ? 1
                : 0,
          },
        };
      })
      .filter(Boolean),
  };
}

function openListingPopup({
  map,
  popup,
  feature,
  listings,
  slug,
  onListingSelect,
  formatListingPrice,
  viewDetailsLabel,
  bedsShortLabel,
  bathsShortLabel,
}) {
  const { id, title, price, currency, thumbnailUrl, bedrooms, bathrooms } = feature.properties;
  const listing = listings.find((row) => String(row.id) === String(id));
  if (onListingSelect) {
    onListingSelect(listing || { id, title, price, currency });
  }

  const priceLabel =
    typeof formatListingPrice === "function"
      ? formatListingPrice(listing || { id, title, price, currency }) || "—"
      : price != null
        ? formatPublicListingPrice({ price: Number(price), currency })
        : "—";

  const detailHref = slug && id ? runtimeListingPath(slug, id) : "#";
  const resolvedThumb = resolvePublicImageUrl(thumbnailUrl);
  const proxyThumbUrl =
    resolvedThumb && resolvedThumb.startsWith("http")
      ? `${API_BASE_URL}/api/media/proxy-image?url=${encodeURIComponent(resolvedThumb)}`
      : null;
  const proxyFallback = proxyThumbUrl
    ? `this.onerror=function(){this.onerror=null;this.src='${PROPERTY_PLACEHOLDER_SRC}';this.classList.add('cv-property-image--fallback');};this.src='${proxyThumbUrl.replace(/'/g, "\\'")}';`
    : `this.onerror=null;this.src='${PROPERTY_PLACEHOLDER_SRC}';this.classList.add('cv-property-image--fallback');`;
  const thumbHtml = resolvedThumb
    ? `<img src="${resolvedThumb.replace(/"/g, "&quot;")}" alt="" class="cv-listing-map-popup__thumb" loading="lazy" onerror="${proxyFallback.replace(/"/g, "&quot;")}" />`
    : `<img src="${PROPERTY_PLACEHOLDER_SRC}" alt="" class="cv-listing-map-popup__thumb cv-property-image--fallback" />`;

  const facts = [
    bedrooms != null ? bedsShortLabel(bedrooms) : null,
    bathrooms != null ? bathsShortLabel(bathrooms) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  popup
    .setLngLat(feature.geometry.coordinates)
    .setHTML(
      `<div class="cv-listing-map-popup">
        ${thumbHtml}
        <div class="cv-listing-map-popup__body">
          <strong>${title}</strong>
          <div style="margin:0.35rem 0 0.5rem;color:#4b5563;">${priceLabel}</div>
          ${facts ? `<div style="color:#6b7280;font-size:0.85rem;margin-bottom:0.5rem;">${facts}</div>` : ""}
          <a href="${detailHref}" style="color:#1f4d3a;font-weight:600;">${viewDetailsLabel}</a>
        </div>
      </div>`
    )
    .addTo(map);
}

const ClientViewListingsMap = ({
  slug,
  listings = [],
  boundaryGeojson = null,
  selectedBoundaryId = null,
  onBoundarySelect = null,
  selectedListingId = null,
  flyToListingId = null,
  popupListing = null,
  renderPopup = null,
  onPopupClose,
  popupSelectionKey = 0,
  onListingSelect,
  onBoundsChange,
  formatListingPrice = null,
  viewDetailsLabel = "View details",
  bedsShortLabel = (count) => `${count} bd`,
  bathsShortLabel = (count) => `${count} ba`,
  center,
  zoom = DEFAULT_ZOOM,
  height = 320,
  /** { west, south, east, north } framed once when the map loads. */
  initialBounds = null,
  fitToListings = true,
  allowEmptyMap = false,
  enableClusters = false,
  aggregates = [],
  aggregateCellSizeDegrees = null,
  ariaLabel = "Listings map",
  markerScale = 1,
  markerOpacity = 0.95,
  showMapControls = false,
}) => {
  const safeMarkerScale =
    Number.isFinite(Number(markerScale)) && Number(markerScale) > 0 ? Number(markerScale) : 1;
  const safeMarkerOpacity = Math.min(
    1,
    Math.max(0, Number.isFinite(Number(markerOpacity)) ? Number(markerOpacity) : 0.95)
  );
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const popupHostRef = useRef(null);
  const [popupHost, setPopupHost] = useState(null);
  const lastPopupIdRef = useRef(null);
  const lastSelectionKeyRef = useRef(popupSelectionKey);
  const suppressPopupCloseRef = useRef(false);
  const closedByUserRef = useRef(null);
  const programmaticMoveRef = useRef(false);
  const initialMoveendRef = useRef(true);
  const ignoreBoundsUntilRef = useRef(0);
  const fitToListingsRef = useRef(fitToListings);
  const onBoundsChangeRef = useRef(onBoundsChange);
  const formatListingPriceRef = useRef(formatListingPrice);
  const viewDetailsLabelRef = useRef(viewDetailsLabel);
  const bedsShortLabelRef = useRef(bedsShortLabel);
  const bathsShortLabelRef = useRef(bathsShortLabel);
  const centerRef = useRef(center);
  const initialBoundsRef = useRef(initialBounds);
  initialBoundsRef.current = initialBounds;
  const listingsRef = useRef(listings);
  const aggregatesRef = useRef(aggregates);
  aggregatesRef.current = aggregates;
  const aggregateCellSizeRef = useRef(aggregateCellSizeDegrees);
  aggregateCellSizeRef.current = aggregateCellSizeDegrees;
  const onListingSelectRef = useRef(onListingSelect);
  const onBoundarySelectRef = useRef(onBoundarySelect);
  onBoundarySelectRef.current = onBoundarySelect;
  const selectedBoundaryIdRef = useRef(selectedBoundaryId);
  selectedBoundaryIdRef.current = selectedBoundaryId;
  const boundaryGeojsonRef = useRef(boundaryGeojson);
  boundaryGeojsonRef.current = boundaryGeojson;
  const flyToListingIdRef = useRef(flyToListingId);
  const renderPopupRef = useRef(renderPopup);
  const onPopupCloseRef = useRef(onPopupClose);
  /** Once true, listing data updates must not re-fit (prevents pan/zoom feedback loops). */
  const userHasMovedRef = useRef(false);
  const [mapReady, setMapReady] = useState(false);
  const [mapZoom, setMapZoom] = useState(zoom);
  const [basemap, setBasemap] = useState("light");
  const savedViewportRef = useRef(null);
  const mapStyle = BASEMAP_STYLES[basemap] || DEFAULT_MAP_STYLE;
  const geolocation = useOptionalClientViewGeolocation();
  useAutoRequestClientViewGeolocation(geolocation);
  useMapUserLocationLayer(mapRef, geolocation?.coordinates || null, mapReady);

  const aggregateGeojson = useMemo(
    () => aggregatesToGeoJson(aggregates, aggregateCellSizeDegrees),
    [aggregates, aggregateCellSizeDegrees]
  );
  const showAggregates = aggregateGeojson.features.length > 0;

  useEffect(() => {
    const wasEnabled = fitToListingsRef.current;
    fitToListingsRef.current = fitToListings;
    if (fitToListings && !wasEnabled) {
      userHasMovedRef.current = false;
      initialMoveendRef.current = true;
      savedViewportRef.current = null;
    }
  }, [fitToListings]);
  useEffect(() => {
    onBoundsChangeRef.current = onBoundsChange;
  }, [onBoundsChange]);
  useEffect(() => {
    formatListingPriceRef.current = formatListingPrice;
  }, [formatListingPrice]);
  useEffect(() => {
    viewDetailsLabelRef.current = viewDetailsLabel;
  }, [viewDetailsLabel]);
  useEffect(() => {
    bedsShortLabelRef.current = bedsShortLabel;
  }, [bedsShortLabel]);
  useEffect(() => {
    bathsShortLabelRef.current = bathsShortLabel;
  }, [bathsShortLabel]);
  useEffect(() => {
    centerRef.current = center;
  }, [center]);
  useEffect(() => {
    listingsRef.current = listings;
  }, [listings]);
  useEffect(() => {
    onListingSelectRef.current = onListingSelect;
  }, [onListingSelect]);
  useEffect(() => {
    flyToListingIdRef.current = flyToListingId;
  }, [flyToListingId]);
  useEffect(() => {
    renderPopupRef.current = renderPopup;
  }, [renderPopup]);
  useEffect(() => {
    onPopupCloseRef.current = onPopupClose;
  }, [onPopupClose]);

  const listingGeojson = useMemo(() => listingsToGeoJson(listings), [listings]);

  const fitMapToFeatures = useCallback((map, features, duration = 0) => {
    const refCenter = centerRef.current || DEFAULT_CENTER;
    const nearby = featuresNearCenter(features, refCenter);
    const toFit = trimOutlierFeatures(nearby.length ? nearby : features);
    if (!toFit.length) return;
    programmaticMoveRef.current = true;
    // fitBounds can emit multiple moveend events; silence bounds sync until settled.
    ignoreBoundsUntilRef.current = Date.now() + Math.max(duration, 0) + 400;
    const bounds = new mapboxgl.LngLatBounds();
    toFit.forEach((f) => bounds.extend(f.geometry.coordinates));
    map.fitBounds(bounds, { padding: 48, maxZoom: 14, duration });
  }, []);

  useEffect(() => {
    if (!MAPBOX_TOKEN || !containerRef.current) {
      return undefined;
    }

    mapboxgl.accessToken = MAPBOX_TOKEN;
    const savedViewport = savedViewportRef.current;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: mapStyle,
      center: savedViewport?.center || center || DEFAULT_CENTER,
      zoom: savedViewport?.zoom ?? zoom,
      dragPan: true,
      scrollZoom: true,
      boxZoom: true,
      keyboard: true,
      touchZoomRotate: true,
    });
    mapRef.current = map;
    const useCardPopup = typeof renderPopupRef.current === "function";
    popupRef.current = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: false,
      focusAfterOpen: false,
      anchor: "bottom",
      offset: useCardPopup
        ? {
            bottom: [0, 14],
            "bottom-left": [0, 14],
            "bottom-right": [0, 14],
            top: [0, -14],
            "top-left": [0, -14],
            "top-right": [0, -14],
            left: [14, 0],
            right: [-14, 0],
            center: [0, 0],
          }
        : 0,
      maxWidth: useCardPopup ? "300px" : "280px",
      className: useCardPopup ? "cm-map-listing-popup" : "",
    });
    popupRef.current.on("close", () => {
      if (suppressPopupCloseRef.current) {
        return;
      }
      closedByUserRef.current =
        lastPopupIdRef.current || flyToListingIdRef.current || true;
      popupHostRef.current = null;
      setPopupHost(null);
      onPopupCloseRef.current?.();
    });

    map.on("load", () => {
      if (mapRef.current !== map) {
        return;
      }
      setMapReady(true);
      if (boundaryGeojson?.features?.length) {
        map.addSource(BOUNDARY_SOURCE, {
          type: "geojson",
          data: boundaryGeojson,
        });
        map.addLayer({
          id: BOUNDARY_FILL_LAYER,
          type: "fill",
          source: BOUNDARY_SOURCE,
          paint: {
            "fill-color": ["coalesce", ["get", "fillColor"], "#5eead4"],
            "fill-opacity": buildBoundaryFillOpacityExpression(selectedBoundaryIdRef.current),
          },
        });
        map.addLayer({
          id: BOUNDARY_LINE_LAYER,
          type: "line",
          source: BOUNDARY_SOURCE,
          paint: {
            "line-color": "#134e4a",
            "line-width": 1.15,
            "line-opacity": buildBoundaryLineOpacityExpression(selectedBoundaryIdRef.current),
          },
        });
        const highlightFilter = buildBoundaryHighlightFilter(selectedBoundaryIdRef.current);
        map.addLayer({
          id: BOUNDARY_HIGHLIGHT_FILL,
          type: "fill",
          source: BOUNDARY_SOURCE,
          filter: highlightFilter,
          paint: {
            "fill-color": "#99f6e4",
            "fill-opacity": 0.16,
          },
        });
        map.addLayer({
          id: BOUNDARY_HIGHLIGHT_HALO,
          type: "line",
          source: BOUNDARY_SOURCE,
          filter: highlightFilter,
          layout: { "line-join": "round", "line-cap": "round" },
          paint: {
            "line-color": "#2dd4bf",
            "line-width": 16,
            "line-opacity": 0.42,
            "line-blur": 7,
            "line-offset": 1,
          },
        });
        map.addLayer({
          id: BOUNDARY_HIGHLIGHT_LINE,
          type: "line",
          source: BOUNDARY_SOURCE,
          filter: highlightFilter,
          layout: { "line-join": "round", "line-cap": "round" },
          paint: {
            "line-color": "#134e4a",
            "line-width": 2.4,
            "line-opacity": 0.95,
          },
        });
        map.addLayer({
          id: BOUNDARY_HIGHLIGHT_CORE,
          type: "line",
          source: BOUNDARY_SOURCE,
          filter: highlightFilter,
          layout: { "line-join": "round", "line-cap": "round" },
          paint: {
            "line-color": "#f8fafc",
            "line-width": 1.15,
            "line-opacity": 0.98,
          },
        });
        map.on("click", BOUNDARY_FILL_LAYER, (event) => {
          const feature = event.features?.[0];
          const props = feature?.properties || {};
          const nextId = normalizeBoundaryId(props.boundaryId || feature?.id);
          if (!nextId) return;
          onBoundarySelectRef.current?.({
            boundaryId: nextId,
            neighborhood: props.name || props.neighborhood || "",
            municipality: props.municipality || "",
          });
        });
        map.on("mouseenter", BOUNDARY_FILL_LAYER, () => {
          map.getCanvas().style.cursor = "pointer";
        });
        map.on("mouseleave", BOUNDARY_FILL_LAYER, () => {
          map.getCanvas().style.cursor = "";
        });
      }

      // Prefer live listings — pins often arrive after map construction, and a
      // closed-over empty FeatureCollection would leave the source blank forever
      // if the listings effect ran before style load.
      const initialListingsGeojson = listingsToGeoJson(listingsRef.current);

      map.addSource(LISTINGS_SOURCE, {
        type: "geojson",
        data: initialListingsGeojson,
        ...(enableClusters
          ? {
              cluster: true,
              clusterMaxZoom: CLUSTER_MAX_ZOOM,
              clusterRadius: CLUSTER_RADIUS,
            }
          : {}),
      });

      if (enableClusters) {
        map.addLayer({
          id: LISTINGS_CLUSTERS,
          type: "circle",
          source: LISTINGS_SOURCE,
          filter: ["has", "point_count"],
          paint: {
            "circle-color": CLUSTER_COLOR,
            "circle-radius": ["step", ["get", "point_count"], 18, 10, 22, 50, 28, 150, 34],
            "circle-opacity": 0.9,
            "circle-stroke-width": 2,
            "circle-stroke-color": "#ffffff",
          },
        });
        map.addLayer({
          id: LISTINGS_CLUSTER_COUNT,
          type: "symbol",
          source: LISTINGS_SOURCE,
          filter: ["has", "point_count"],
          layout: {
            "text-field": ["get", "point_count_abbreviated"],
            "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
            "text-size": 12,
            "text-allow-overlap": true,
          },
          paint: {
            "text-color": "#ffffff",
          },
        });
      }

      const unclusteredFilter = enableClusters
        ? ["!", ["has", "point_count"]]
        : ["all"];

      map.addLayer({
        id: LISTINGS_HALO,
        type: "circle",
        source: LISTINGS_SOURCE,
        filter: unclusteredFilter,
        paint: {
          "circle-radius": [
            "case",
            ["==", ["get", "id"], String(selectedListingId || "")],
            16 * safeMarkerScale,
            12 * safeMarkerScale,
          ],
          "circle-color": "#ffffff",
          "circle-opacity": safeMarkerOpacity,
        },
      });

      map.addLayer({
        id: LISTINGS_POINT,
        type: "circle",
        source: LISTINGS_SOURCE,
        filter: unclusteredFilter,
        paint: {
          "circle-radius": [
            "case",
            ["==", ["get", "id"], String(selectedListingId || "")],
            10 * safeMarkerScale,
            7 * safeMarkerScale,
          ],
          "circle-color": [
            "case",
            ["==", ["get", "id"], String(selectedListingId || "")],
            "#b8860b",
            [">", ["to-number", ["get", "approximateLocation"]], 0],
            APPROXIMATE_LOCATION_COLOR,
            ["!", ["to-boolean", ["get", "hasAddress"]]],
            MISSING_ADDRESS_COLOR,
            CLUSTER_COLOR,
          ],
          "circle-stroke-width": 2,
          "circle-stroke-color": "#ffffff",
          "circle-opacity": safeMarkerOpacity,
        },
      });

      if (enableClusters) {
        map.on("click", LISTINGS_CLUSTERS, (event) => {
          const feature = event.features?.[0];
          const clusterId = feature?.properties?.cluster_id;
          const source = map.getSource(LISTINGS_SOURCE);
          if (clusterId == null || !source?.getClusterExpansionZoom) {
            return;
          }
          source.getClusterExpansionZoom(clusterId, (error, nextZoom) => {
            if (error || nextZoom == null) {
              return;
            }
            programmaticMoveRef.current = true;
            ignoreBoundsUntilRef.current = Date.now() + 700;
            map.easeTo({
              center: feature.geometry.coordinates,
              zoom: Math.min(Number(nextZoom), 16),
              duration: 450,
            });
          });
        });
        map.on("mouseenter", LISTINGS_CLUSTERS, () => {
          map.getCanvas().style.cursor = "pointer";
        });
        map.on("mouseleave", LISTINGS_CLUSTERS, () => {
          map.getCanvas().style.cursor = "";
        });
      }

      map.on("click", LISTINGS_POINT, (event) => {
        const feature = event.features?.[0];
        if (!feature) {
          return;
        }
        if (typeof renderPopupRef.current === "function") {
          const listing = listingsRef.current.find(
            (row) => String(row.id) === String(feature.properties.id)
          );
          onListingSelectRef.current?.(listing || { id: feature.properties.id });
          return;
        }
        openListingPopup({
          map,
          popup: popupRef.current,
          feature,
          listings: listingsRef.current,
          slug,
          onListingSelect: onListingSelectRef.current,
          formatListingPrice: formatListingPriceRef.current,
          viewDetailsLabel: viewDetailsLabelRef.current,
          bedsShortLabel: bedsShortLabelRef.current,
          bathsShortLabel: bathsShortLabelRef.current,
        });
      });
      map.on("click", (event) => {
        if (typeof renderPopupRef.current !== "function") {
          return;
        }
        const layers = [LISTINGS_POINT, LISTINGS_CLUSTERS].filter((id) => map.getLayer(id));
        const hits = layers.length ? map.queryRenderedFeatures(event.point, { layers }) : [];
        if (!hits.length) {
          popupRef.current?.remove();
        }
      });

      map.on("mouseenter", LISTINGS_POINT, () => {
        map.getCanvas().style.cursor = "pointer";
      });
      map.on("mouseleave", LISTINGS_POINT, () => {
        map.getCanvas().style.cursor = "";
      });

      map.on("moveend", () => {
        const programmatic =
          programmaticMoveRef.current ||
          Date.now() < ignoreBoundsUntilRef.current ||
          initialMoveendRef.current;
        initialMoveendRef.current = false;
        if (programmatic) {
          programmaticMoveRef.current = false;
        } else {
          userHasMovedRef.current = true;
          savedViewportRef.current = {
            center: [map.getCenter().lng, map.getCenter().lat],
            zoom: map.getZoom(),
          };
        }
        const cb = onBoundsChangeRef.current;
        if (cb) {
          const b = map.getBounds();
          const nextZoom = map.getZoom();
          setMapZoom(nextZoom);
          cb({
            north: b.getNorth(),
            south: b.getSouth(),
            east: b.getEast(),
            west: b.getWest(),
            zoom: nextZoom,
            programmatic,
          });
        }
      });

      const initialAggregateGeojson = aggregatesToGeoJson(
        aggregatesRef.current,
        aggregateCellSizeRef.current
      );
      const showInitialAggregates = initialAggregateGeojson.features.length > 0;
      map.addSource(AGGREGATE_SOURCE, {
        type: "geojson",
        data: initialAggregateGeojson,
      });
      map.addLayer({
        id: AGGREGATE_CIRCLES,
        type: "circle",
        source: AGGREGATE_SOURCE,
        paint: {
          "circle-color": CLUSTER_COLOR,
          "circle-radius": [
            "interpolate",
            ["linear"],
            ["get", "count"],
            1,
            16,
            25,
            20,
            100,
            26,
            500,
            32,
            2000,
            40,
          ],
          "circle-opacity": 0.9,
          "circle-stroke-width": 2,
          "circle-stroke-color": "#ffffff",
        },
        layout: { visibility: showInitialAggregates ? "visible" : "none" },
      });
      map.addLayer({
        id: AGGREGATE_COUNT,
        type: "symbol",
        source: AGGREGATE_SOURCE,
        layout: {
          "text-field": ["get", "count_label"],
          "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
          "text-size": 12,
          "text-allow-overlap": true,
          visibility: showInitialAggregates ? "visible" : "none",
        },
        paint: {
          "text-color": "#ffffff",
        },
      });

      map.on("click", AGGREGATE_CIRCLES, (event) => {
        const feature = event.features?.[0];
        if (!feature?.geometry?.coordinates) {
          return;
        }
        const [lng, lat] = feature.geometry.coordinates;
        const cellSize = Number(feature.properties?.cell_size_degrees || 0.06);
        const half = cellSize / 2;
        programmaticMoveRef.current = true;
        ignoreBoundsUntilRef.current = Date.now() + 700;
        try {
          map.fitBounds(
            [
              [lng - half, lat - half],
              [lng + half, lat + half],
            ],
            {
              padding: 56,
              maxZoom: Number(feature.properties?.count) <= 250 ? AGGREGATE_PIN_ZOOM : 13.5,
              duration: 550,
            }
          );
        } catch {
          map.easeTo({
            center: [lng, lat],
            zoom: Math.min(map.getZoom() + 2, AGGREGATE_PIN_ZOOM),
            duration: 450,
          });
        }
      });
      map.on("mouseenter", AGGREGATE_CIRCLES, () => {
        map.getCanvas().style.cursor = "pointer";
      });
      map.on("mouseleave", AGGREGATE_CIRCLES, () => {
        map.getCanvas().style.cursor = "";
      });

      const openingBounds = initialBoundsRef.current;
      let didMoveCamera = false;
      if (savedViewport?.center) {
        programmaticMoveRef.current = true;
        ignoreBoundsUntilRef.current = Date.now() + 400;
        map.jumpTo({ center: savedViewport.center, zoom: savedViewport.zoom });
        didMoveCamera = true;
      } else if (
        openingBounds &&
        [openingBounds.west, openingBounds.south, openingBounds.east, openingBounds.north].every(
          (value) => Number.isFinite(Number(value))
        )
      ) {
        programmaticMoveRef.current = true;
        ignoreBoundsUntilRef.current = Date.now() + 400;
        map.fitBounds(
          [
            [Number(openingBounds.west), Number(openingBounds.south)],
            [Number(openingBounds.east), Number(openingBounds.north)],
          ],
          { padding: 32, duration: 0, maxZoom: 13 }
        );
        didMoveCamera = true;
      } else if (fitToListingsRef.current && initialListingsGeojson.features.length) {
        fitMapToFeatures(map, initialListingsGeojson.features, 0);
        didMoveCamera = true;
      }

      // Search waits on the first bounds event before fetching aggregates/pins.
      // When the camera never moves (empty first paint, no saved viewport), Mapbox
      // may not emit moveend after load — publish the opening viewport explicitly.
      if (!didMoveCamera) {
        const cb = onBoundsChangeRef.current;
        if (cb) {
          const b = map.getBounds();
          const nextZoom = map.getZoom();
          setMapZoom(nextZoom);
          cb({
            north: b.getNorth(),
            south: b.getSouth(),
            east: b.getEast(),
            west: b.getWest(),
            zoom: nextZoom,
            programmatic: true,
          });
        }
      }
    });

    return () => {
      suppressPopupCloseRef.current = true;
      popupRef.current?.remove();
      suppressPopupCloseRef.current = false;
      popupRef.current = null;
      popupHostRef.current = null;
      lastPopupIdRef.current = null;
      closedByUserRef.current = null;
      setPopupHost(null);
      try {
        map.remove();
      } catch {
        // Already removed.
      }
      if (mapRef.current === map) {
        mapRef.current = null;
      }
      if (!savedViewportRef.current) {
        userHasMovedRef.current = false;
      }
      setMapReady(false);
    };
    // Recreate only when style/identity changes (basemap, slug, cluster mode).
    // Listing and aggregate updates apply in place. Parent search must not
    // remount this component when the pin set or filters change.
    // fitToListings / onBoundsChange are read via refs to avoid teardown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundaryGeojson, center, zoom, slug, enableClusters, mapStyle]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    let frameId = 0;
    const resizeActiveMap = () => {
      // Always read the live map instance — a stale closure can point at a
      // removed Mapbox map after remount (agency directory multi-select, etc.).
      safeMapResize(mapRef.current);
    };

    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(resizeActiveMap);
    });
    observer.observe(container);
    // Layout can settle after first paint (SPA full-height workspace).
    frameId = requestAnimationFrame(resizeActiveMap);

    return () => {
      cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  }, [
    height,
    slug,
    enableClusters,
    boundaryGeojson,
    center,
    zoom,
    safeMarkerScale,
    safeMarkerOpacity,
  ]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return undefined;
    }

    const applyListingsToMap = () => {
      const source = map.getSource(LISTINGS_SOURCE);
      if (!source) {
        return;
      }
      source.setData(listingGeojson);
      // Never re-fit after the user pans/zooms — updates would yank the camera
      // back and retrigger bounds sync ("refreshing" feedback loop).
      const fitFeatures = [
        ...(listingGeojson.features || []),
        ...(aggregatesRef.current
          ? aggregatesToGeoJson(aggregatesRef.current, aggregateCellSizeRef.current).features
          : []),
      ];
      if (fitToListingsRef.current && !userHasMovedRef.current && fitFeatures.length) {
        fitMapToFeatures(map, fitFeatures, 800);
      }
    };

    if (map.isStyleLoaded() && map.getSource(LISTINGS_SOURCE)) {
      applyListingsToMap();
      return undefined;
    }

    // Listings often resolve while the style is still loading; retry on idle/load
    // so we do not keep an empty source from the initial mount race.
    map.once("idle", applyListingsToMap);
    return () => {
      map.off("idle", applyListingsToMap);
    };
  }, [listingGeojson, fitMapToFeatures, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return undefined;
    }

    const applyAggregatesToMap = () => {
      const aggregateSource = map.getSource(AGGREGATE_SOURCE);
      if (!aggregateSource) {
        return;
      }
      aggregateSource.setData(aggregateGeojson);
      setLayerVisibility(map, AGGREGATE_CIRCLES, showAggregates);
      setLayerVisibility(map, AGGREGATE_COUNT, showAggregates);
      setLayerVisibility(map, LISTINGS_CLUSTERS, !showAggregates && enableClusters);
      setLayerVisibility(map, LISTINGS_CLUSTER_COUNT, !showAggregates && enableClusters);
      setLayerVisibility(map, LISTINGS_HALO, true);
      setLayerVisibility(map, LISTINGS_POINT, true);
      const fitFeatures = [
        ...(listingsToGeoJson(listingsRef.current).features || []),
        ...(aggregateGeojson.features || []),
      ];
      if (fitToListingsRef.current && !userHasMovedRef.current && fitFeatures.length) {
        fitMapToFeatures(map, fitFeatures, 800);
      }
    };

    if (map.isStyleLoaded?.() && map.getSource(AGGREGATE_SOURCE)) {
      applyAggregatesToMap();
      return undefined;
    }

    map.once("idle", applyAggregatesToMap);
    return () => {
      map.off("idle", applyAggregatesToMap);
    };
  }, [aggregateGeojson, enableClusters, showAggregates, mapReady, fitMapToFeatures]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.isStyleLoaded() || !map.getLayer(LISTINGS_POINT)) {
      return;
    }
    const selected = String(selectedListingId || "");
    map.setPaintProperty(LISTINGS_POINT, "circle-radius", [
      "case",
      ["==", ["get", "id"], selected],
      10 * safeMarkerScale,
      7 * safeMarkerScale,
    ]);
    map.setPaintProperty(LISTINGS_POINT, "circle-opacity", safeMarkerOpacity);
    map.setPaintProperty(LISTINGS_POINT, "circle-color", [
      "case",
      ["==", ["get", "id"], selected],
      "#b8860b",
      [">", ["to-number", ["get", "approximateLocation"]], 0],
      APPROXIMATE_LOCATION_COLOR,
      ["!", ["to-boolean", ["get", "hasAddress"]]],
      MISSING_ADDRESS_COLOR,
      CLUSTER_COLOR,
    ]);
    if (map.getLayer(LISTINGS_HALO)) {
      map.setPaintProperty(LISTINGS_HALO, "circle-radius", [
        "case",
        ["==", ["get", "id"], selected],
        16 * safeMarkerScale,
        12 * safeMarkerScale,
      ]);
      map.setPaintProperty(LISTINGS_HALO, "circle-opacity", safeMarkerOpacity);
    }
  }, [selectedListingId, safeMarkerScale, safeMarkerOpacity]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;

    const applySelection = () => {
      if (!map.getLayer(BOUNDARY_FILL_LAYER)) {
        return false;
      }
      const selectedId = normalizeBoundaryId(selectedBoundaryId);
      const highlightFilter = buildBoundaryHighlightFilter(selectedId);
      try {
        map.setPaintProperty(
          BOUNDARY_FILL_LAYER,
          "fill-opacity",
          buildBoundaryFillOpacityExpression(selectedId)
        );
        map.setPaintProperty(
          BOUNDARY_LINE_LAYER,
          "line-opacity",
          buildBoundaryLineOpacityExpression(selectedId)
        );
        [BOUNDARY_HIGHLIGHT_FILL, BOUNDARY_HIGHLIGHT_HALO, BOUNDARY_HIGHLIGHT_LINE, BOUNDARY_HIGHLIGHT_CORE].forEach((layerId) => {
          if (map.getLayer(layerId)) {
            map.setFilter(layerId, highlightFilter);
          }
        });
      } catch {
        return false;
      }

      if (!selectedId) return true;
      const feature = findBoundaryFeature(boundaryGeojsonRef.current, { boundaryId: selectedId });
      if (!feature?.geometry) return true;
      const bounds = extendLngLatBounds(new mapboxgl.LngLatBounds(), feature.geometry);
      const west = bounds?.getWest?.();
      const east = bounds?.getEast?.();
      const south = bounds?.getSouth?.();
      const north = bounds?.getNorth?.();
      if (![west, east, south, north].every(Number.isFinite) || (west === east && south === north)) {
        return true;
      }
      programmaticMoveRef.current = true;
      ignoreBoundsUntilRef.current = Date.now() + 900;
      map.fitBounds(bounds, {
        padding: { top: 80, bottom: 80, left: 80, right: 80 },
        maxZoom: 13.1,
        duration: 750,
      });
      return true;
    };

    if (applySelection()) return undefined;
    const onIdle = () => {
      applySelection();
    };
    map.once("idle", onIdle);
    return () => {
      map.off("idle", onIdle);
    };
  }, [selectedBoundaryId, mapReady]);

  useEffect(() => {
    const useCardPopup = typeof renderPopupRef.current === "function";
    const fromMap = flyToListingId
      ? listingsRef.current.find((row) => String(row.id) === String(flyToListingId))
      : null;
    const listingId = String(
      popupListing?.id ?? fromMap?.id ?? flyToListingId ?? ""
    );
    const pinZoom = CLUSTER_MAX_ZOOM + 1;

    const closeCardPopup = () => {
      const livePopup = popupRef.current;
      if (livePopup && popupIsOpen(livePopup)) {
        suppressPopupCloseRef.current = true;
        livePopup.remove();
        suppressPopupCloseRef.current = false;
      }
      lastPopupIdRef.current = null;
      popupHostRef.current = null;
      setPopupHost(null);
    };

    const flyToPin = (liveMap, targetCoords) => {
      programmaticMoveRef.current = true;
      ignoreBoundsUntilRef.current = Date.now() + 800;
      liveMap.easeTo({
        center: targetCoords,
        zoom: Math.max(liveMap.getZoom(), pinZoom),
        duration: 450,
        offset: useCardPopup ? [0, 110] : [0, 0],
      });
    };

    const resolveCoords = (liveMap) =>
      listingCoordinates(popupListing) ||
      listingCoordinates(fromMap) ||
      queryRenderedListingCoordinates(liveMap, listingId);

    if (useCardPopup && !listingId) {
      closedByUserRef.current = null;
      closeCardPopup();
      return undefined;
    }

    // A new explicit selection (card/marker click) should reopen the card.
    if (lastSelectionKeyRef.current !== popupSelectionKey) {
      lastSelectionKeyRef.current = popupSelectionKey;
      closedByUserRef.current = null;
    }

    const attach = () => {
      const liveMap = mapRef.current;
      const livePopup = popupRef.current;
      const coords = resolveCoords(liveMap);
      if (!liveMap?.isStyleLoaded() || !listingId || !coords) {
        return false;
      }

      if (!useCardPopup) {
        if (lastPopupIdRef.current !== listingId) {
          lastPopupIdRef.current = listingId;
          flyToPin(liveMap, coords);
        }
        return true;
      }

      if (!livePopup || !popupListing) {
        return false;
      }

      if (closedByUserRef.current) {
        const closedId = String(closedByUserRef.current);
        if (closedId === listingId || closedByUserRef.current === true) {
          return true;
        }
      }

      const listingChanged = lastPopupIdRef.current !== listingId;
      const alreadyOpen = popupIsOpen(livePopup);
      lastPopupIdRef.current = listingId;
      livePopup.setLngLat(coords);

      let host = popupHostRef.current;
      if (!host) {
        host = document.createElement("div");
        host.className = "cm-map-listing-popup__root";
        popupHostRef.current = host;
        livePopup.setDOMContent(host);
        setPopupHost(host);
      }

      if (!alreadyOpen) {
        livePopup.addTo(liveMap);
      }

      if (listingChanged) {
        flyToPin(liveMap, coords);
      }
      return true;
    };

    if (attach()) {
      return undefined;
    }

    const liveMap = mapRef.current;
    if (!liveMap || !listingId) {
      return undefined;
    }
    const retry = () => {
      attach();
    };
    liveMap.once("idle", retry);
    liveMap.once("load", retry);
    return () => {
      liveMap.off("idle", retry);
      liveMap.off("load", retry);
    };
    // Depend on listing identity/coordinates, not object identity — parent
    // listing refreshes were re-attaching the popup and making it flash.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mapReady,
    flyToListingId,
    popupSelectionKey,
    popupListing?.id,
    popupListing?.latitude,
    popupListing?.longitude,
  ]);

  if (!MAPBOX_TOKEN) {
    return (
      <div className="cv-listings-map-placeholder panel-card" style={{ minHeight: height }}>
        <p className="muted">Map requires REACT_APP_MAPBOX_ACCESS_TOKEN.</p>
      </div>
    );
  }

  if (!listingGeojson.features.length && !allowEmptyMap) {
    return (
      <div className="cv-listings-map-placeholder panel-card" style={{ minHeight: height }}>
        <p className="muted">No mappable listings for this search.</p>
      </div>
    );
  }

  const rememberViewport = () => {
    const map = mapRef.current;
    if (!map) return;
    savedViewportRef.current = {
      center: [map.getCenter().lng, map.getCenter().lat],
      zoom: map.getZoom(),
    };
    userHasMovedRef.current = true;
  };

  const handleZoomIn = () => {
    rememberViewport();
    mapRef.current?.zoomIn({ duration: 250 });
  };

  const handleZoomOut = () => {
    rememberViewport();
    mapRef.current?.zoomOut({ duration: 250 });
  };

  const handlePan = (dx, dy) => {
    rememberViewport();
    mapRef.current?.panBy([dx, dy], { duration: 280 });
  };

  const handleBasemapChange = (nextBasemap) => {
    if (nextBasemap === basemap) return;
    rememberViewport();
    setBasemap(nextBasemap);
  };

  return (
    <>
      <div
        className={`cv-listings-map-shell${showMapControls ? " cv-listings-map-shell--controls" : ""}`}
        style={{ height, width: "100%", minHeight: height }}
      >
        <div
          ref={containerRef}
          className="cv-listings-map"
          style={{ height: "100%", width: "100%", minHeight: height }}
          aria-label={ariaLabel}
        />
        {showMapControls ? (
          <CasaMapaMapControls
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onPan={handlePan}
            basemap={basemap}
            onBasemapChange={handleBasemapChange}
          />
        ) : null}
      </div>
      {popupHost && popupListing && typeof renderPopup === "function"
        ? createPortal(renderPopup(popupListing), popupHost)
        : null}
    </>
  );
};

export default ClientViewListingsMap;
